// #region Imports
import { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import type { AZCollapseProps, AZDropdownProps } from './AZDropdown.types';
import './AZDropdown.css';
// #endregion

// #region AZCollapse — standalone collapsible content region
/**
 * Collapsible content region. Can be used standalone or inside AZDropdown.
 *
 * @example
 * <AZCollapse id="section-1" isOpen={isOpen}>
 *   <p>Content</p>
 * </AZCollapse>
 */
const AZCollapse = ({ children, id, isOpen }: AZCollapseProps) => (
  <div
    className="az-dropdown-content"
    id={String(id)}
    data-state={isOpen ? 'open' : 'closed'}
    hidden={!isOpen}
  >
    {isOpen ? children : null}
  </div>
);

AZCollapse.displayName = 'AZCollapse';
// #endregion

// #region AZDropdown
/**
 * Collapsible section with a trigger button. Replaces React Bootstrap Collapse.
 * When `children` is provided, manages open state internally.
 * When `onClick` is provided, delegates open logic to the consumer.
 *
 * @example
 * <AZDropdown libelle="Section 1">
 *   <p>Hidden content</p>
 * </AZDropdown>
 */
const AZDropdown = ({
  libelle,
  children,
  id,
  color,
  buttonColor,
  onClick,
  defaultOpen = false,
}: AZDropdownProps) => {
  // #region State
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // #endregion

  // #region Events
  const handleChange = () => {
    if (onClick) {
      onClick(id);
      return;
    }
    if (children) setIsOpen((prev) => !prev);
  };
  // #endregion

  // #region Interface
  return (
    <div>
      <button
        type="button"
        className="az-dropdown-trigger"
        data-state={isOpen ? 'open' : 'closed'}
        style={{
          color: color ?? undefined,
          backgroundColor: buttonColor ?? undefined,
        }}
        onClick={handleChange}
        aria-expanded={isOpen}
        aria-controls={id != null ? String(id) : undefined}
      >
        {libelle}
        {children && <FaChevronDown className="az-dropdown-chevron" aria-hidden="true" />}
      </button>
      {children && (
        <AZCollapse id={id ?? 'az-collapse'} isOpen={isOpen}>
          {children}
        </AZCollapse>
      )}
    </div>
  );
  // #endregion
};
// #endregion

// #region Display Name
AZDropdown.displayName = 'AZDropdown';
// #endregion

export { AZDropdown, AZCollapse };
